import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LanguageToggle from './LanguageToggle';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { href: '/', label: t('nav_home') },
    { href: '/product', label: t('nav_product') },
    { href: '/how-it-works', label: t('nav_how_it_works') },
    { href: '/company', label: t('nav_company') },
    { href: '/contact', label: t('nav_contact') },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-fac-dark/90 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo192.png" alt="FAC Solutions" className="w-10 h-10 object-contain" />
            <span className="font-heading font-bold text-xl text-white tracking-tight hidden sm:inline">
              FAC<span className="text-fac-green">VALVE</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link, index) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-heading text-sm uppercase tracking-wider transition-colors ${
                  isActive(link.href)
                    ? 'text-fac-green'
                    : 'text-white hover:text-fac-green'
                }`}
                data-testid={`nav-link-${index}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageToggle />
            {user ? (
              <div className="flex items-center gap-4">
                <Link to="/app">
                  <Button 
                    className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim"
                    data-testid="nav-dashboard-btn"
                  >
                    <span>{t('nav_dashboard')}</span>
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:text-fac-red"
                  data-testid="nav-logout-btn"
                >
                  {t('nav_logout')}
                </Button>
              </div>
            ) : (
              <Link to="/login">
                <Button 
                  className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim"
                  data-testid="nav-login-btn"
                >
                  <span>{t('nav_login')}</span>
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageToggle />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6 text-white" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-fac-dark border-border w-[280px]">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-heading text-lg uppercase tracking-wider ${
                        isActive(link.href)
                          ? 'text-fac-green'
                          : 'text-white hover:text-fac-green'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-border pt-6 mt-4">
                    {user ? (
                      <>
                        <Link to="/app" onClick={() => setIsOpen(false)}>
                          <Button className="w-full mb-3 bg-fac-green text-black font-bold">
                            {t('nav_dashboard')}
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          className="w-full border-fac-red text-fac-red"
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                        >
                          {t('nav_logout')}
                        </Button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setIsOpen(false)}>
                        <Button className="w-full bg-fac-green text-black font-bold">
                          {t('nav_login')}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
